// The test console's right-hand column: what the turn understood, and the raw
// debug payload behind it.

import { useState } from "react";

import { Badge, Tabs } from "../ui";
import { titleCase } from "./shared";
import type { SimulationMessage, SimulationPayload } from "./console_model";

const LANG_LABELS: Record<string, string> = {
  en: "English",
  hi: "Hindi",
  hing: "Hinglish",
  bn: "Bengali",
  bnro: "Bengali (roman)",
};
const langLabel = (s: string) => LANG_LABELS[String(s || "")] ?? titleCase(s);

function pretty(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value, null, 2);
}

const CONSOLE_TABS = [
  { id: "session", label: "Session" },
  { id: "quality", label: "Quality" },
  { id: "trace", label: "Trace" },
  { id: "sources", label: "Sources" },
  { id: "guardrails", label: "Guardrails" },
  { id: "eval", label: "Eval notes" },
];

export function DebugPanel({
  payload,
  latestUserMessage,
}: {
  payload: SimulationPayload | null;
  latestUserMessage: SimulationMessage | undefined;
}) {
  const [tab, setTab] = useState("session");

  const debug = payload?.debug ?? {};
  const analysis = debug.analysis ?? {};
  const hasAnalysis = Boolean(
    analysis.emotion || analysis.journey || analysis.persona_archetype || analysis.response_style || analysis.classification
  );
  const session = payload?.session ?? debug.session ?? {};
  const trace = debug.trace ?? [];
  const sources = debug.sources ?? [];
  const guardrails = debug.guardrails ?? [];
  const evalNotes = debug.eval_notes ?? {};

  return (
    <div className="stack">
      <div className="card">
        <h3 style={{ marginBottom: "var(--sp-3)" }}>Conversation analysis</h3>
        {!hasAnalysis ? (
          <p className="muted" style={{ marginBottom: 0 }}>
            Send a turn to see the detected emotion, how the bot is mirroring, and the journey.
          </p>
        ) : (
          <>
            <div className="section">
              <h4>Emotion detected</h4>
              <div className="chips">
                <Badge tone="info">{analysis.emotion ? titleCase(analysis.emotion) : "Neutral"}</Badge>
                {analysis.emotion_language && <Badge tone="neutral">{langLabel(analysis.emotion_language)}</Badge>}
              </div>
            </div>
            <div className="section">
              <h4>How we are responding (mirroring)</h4>
              <div className="chips">
                {analysis.persona_archetype && <Badge tone="info">Persona: {titleCase(analysis.persona_archetype)}</Badge>}
                {analysis.response_style && <Badge tone="good">Style: {titleCase(analysis.response_style)}</Badge>}
                {Number(analysis.persona_confidence ?? 0) > 0 && (
                  <span className="muted" style={{ fontSize: 11 }}>
                    {Math.round(Number(analysis.persona_confidence) * 100)}% confidence
                  </span>
                )}
              </div>
              {!analysis.persona_archetype && !analysis.response_style && (
                <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                  Default style (no persona-specific mirroring yet).
                </p>
              )}
            </div>
            <div className="section">
              <h4>Journey and intent</h4>
              <div className="chips">
                {analysis.journey && <Badge tone="warning">Journey: {titleCase(analysis.journey)}</Badge>}
                {analysis.classification && (
                  <Badge tone={analysis.classification !== "no_scam" ? "critical" : "neutral"}>
                    {titleCase(analysis.classification)}
                  </Badge>
                )}
                {analysis.loss_assessment && <Badge tone="neutral">{titleCase(analysis.loss_assessment)}</Badge>}
                {!analysis.journey && !analysis.classification && <span className="muted">-</span>}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <Tabs tabs={CONSOLE_TABS} active={tab} onChange={setTab} />

        {tab === "session" &&
          (Object.keys(session).length === 0 ? (
            <p className="muted">No session data yet.</p>
          ) : (
            Object.entries(session).map(([key, value]) => (
              <div className="card" key={key} style={{ marginBottom: "var(--sp-2)" }}>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  {key}
                </div>
                <pre className="pre-block">{pretty(value)}</pre>
              </div>
            ))
          ))}

        {tab === "quality" &&
          (Object.keys(debug.quality ?? {}).length === 0 ? (
            <p className="muted">No quality signals on this turn.</p>
          ) : (
            Object.entries(debug.quality ?? {}).map(([key, value]) => (
              <div className="card" key={key} style={{ marginBottom: "var(--sp-2)" }}>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  {key}
                </div>
                <div className="strong">{pretty(value)}</div>
              </div>
            ))
          ))}

        {tab === "trace" &&
          (trace.length === 0 ? (
            <p className="muted">No trace captured for this turn.</p>
          ) : (
            trace.map((item, i) => (
              <div className="card" key={`${item.stage}-${i}`} style={{ marginBottom: "var(--sp-2)" }}>
                <div className="row">
                  <span className="strong" style={{ flex: 1 }}>
                    {item.stage}
                  </span>
                  <Badge tone="neutral">{item.status}</Badge>
                </div>
                <p className="muted" style={{ marginBottom: 0 }}>
                  {item.summary}
                </p>
              </div>
            ))
          ))}

        {tab === "sources" &&
          (sources.length === 0 ? (
            <p className="muted">No sources attached to this turn.</p>
          ) : (
            <pre className="pre-block">{pretty(sources)}</pre>
          ))}

        {tab === "guardrails" &&
          (guardrails.length === 0 ? (
            <p className="muted">No guardrail ran on this turn.</p>
          ) : (
            guardrails.map((item, i) => (
              <div className="card" key={`${item.name}-${i}`} style={{ marginBottom: "var(--sp-2)" }}>
                <div className="row">
                  <span className="strong" style={{ flex: 1 }}>
                    {item.name}
                  </span>
                  <Badge tone="neutral">{item.decision}</Badge>
                </div>
                <p className="muted" style={{ marginBottom: 0 }}>
                  {item.summary}
                </p>
              </div>
            ))
          ))}

        {tab === "eval" && (
          <>
            <div className="card" style={{ marginBottom: "var(--sp-2)" }}>
              <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                Latest user turn
              </div>
              <p className="prewrap" style={{ marginBottom: 0 }}>
                {latestUserMessage?.content || "-"}
              </p>
            </div>
            <div className="card">
              <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                Notes
              </div>
              <pre className="pre-block">{pretty(evalNotes)}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
